import axiosConfig from '../../axiosConfig';
import TokenManager from '../tokenManager';

let isRefreshingToken = false;
let refreshPromise = null;

/**
 * Base API service class with common HTTP methods
 * Provides a consistent interface for all API calls
 */
class BaseApiService {
  constructor(endpoint) {
    this.endpoint = endpoint;
  }

  /**
   * GET request
   * @param {string} path - Additional path to append to endpoint
   * @param {object} params - Query parameters
   */
  async get(path = '', params = {}, headers = {}) {
    try {
      const response = await axiosConfig.get(`${this.endpoint}${path}`, { params, headers });
      
      // Check for FeatureUnauthorized and attempt token refresh
      // if (response.data?.code === 'FeatureUnauthorized' && 
      //     localStorage.getItem('skai1AccessToken') && 
      //     localStorage.getItem('userData')) {
        
      //   const refreshed = await this.attemptTokenRefresh();
      //   if (refreshed) {
      //     // Retry the original request with new token
      //     const retryResponse = await axiosConfig.get(`${this.endpoint}${path}`, { params, headers });
      //     return retryResponse.data;
      //   }
      // }
      
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * POST request
   * @param {string} path - Additional path to append to endpoint
   * @param {object} data - Request body data
   */
  async post(path = '', data = {}, headers = {}) {
    try {
      const response = await axiosConfig.post(`${this.endpoint}${path}`, data, { headers });
      
      // Check for FeatureUnauthorized and attempt token refresh
      // if (response.data?.code === 'FeatureUnauthorized' && 
      //     localStorage.getItem('skai1AccessToken') && 
      //     localStorage.getItem('userData')) {
        
      //   const refreshed = await this.attemptTokenRefresh();
      //   if (refreshed) {
      //     // Retry the original request with new token
      //     const retryResponse = await axiosConfig.post(`${this.endpoint}${path}`, data, { headers });
      //     return retryResponse.data;
      //   }
      // }
      
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * PUT request
   * @param {string} path - Additional path to append to endpoint
   * @param {object} data - Request body data
   * @param {object} headers - Optional headers
   */
  async put(path = '', data = {}, headers = {}) {
    try {
      const response = await axiosConfig.put(`${this.endpoint}${path}`, data, { headers });
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * DELETE request
   * @param {string} path - Additional path to append to endpoint
   */
  async delete(path = '') {
    try {
      console.log(`${this.endpoint}${path}`);
      
      const response = await axiosConfig.delete(`${this.endpoint}${path}`);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * PATCH request
   * @param {string} path - Additional path to append to endpoint
   * @param {object} data - Request body data
   */
  async patch(path = '', data = {}) {
    try {
      const response = await axiosConfig.patch(`${this.endpoint}${path}`, data);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Attempt to refresh the authentication token
   * Uses mutex to prevent multiple simultaneous refresh calls
   * @returns {boolean} True if refresh was successful
   */
  async attemptTokenRefresh() {
    // If already refreshing, wait for the existing refresh to complete
    if (isRefreshingToken && refreshPromise) {
      return refreshPromise;
    }

    isRefreshingToken = true;
    
    refreshPromise = (async () => {
      try {
        // Import accountService dynamically to avoid circular dependency
        const { default: accountService } = await import('./accountService');
        
        const response = await accountService.refreshToken();
        
        if (response.code === 'OK') {
          localStorage.setItem('userData', JSON.stringify(response.data));
          localStorage.setItem('skai1AccessToken', response.data.token);
          
          TokenManager.setTokens(response.data.token);
          
          return true;
        }
        
        this.forceLogout();
        return false;
      } catch (error) {
        console.error('Token refresh failed:', error);
        this.forceLogout();
        return false;
      } finally {
        isRefreshingToken = false;
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  }

  /**
   * Force user logout by clearing all authentication data
   */
  forceLogout() {
    sessionStorage.clear();
    TokenManager.clearTokens();
    window.location.href = '/sign/login';
  }

  /**
   * Handle API errors consistently
   * @param {object} error - Axios error object
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      console.error(`API Error ${error.response.status}:`, error.response.data);
    } else if (error.request) {
      // Request was made but no response received
      console.error('Network Error:', error.request);
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }
  }
}

export default BaseApiService;
